import { useState, useEffect } from "react";
import { ShieldAlert } from "lucide-react";
import Button from "../../../../components/ui/Button";

const ChallengeQuestionModal = ({
  isOpen,
  challengeEvent,
  onSubmitAnswer,
  onTimeout,
}) => {
  const [responseValue, setResponseValue] = useState("");
  const [timeLeft, setTimeLeft] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const eventId = challengeEvent?.event_id;
  const prompt = challengeEvent?.prompt;
  const timeLimit = challengeEvent?.time_limit_seconds || 30;

  // Intercept Escape key press to block closing
  useEffect(() => {
    if (!isOpen || !challengeEvent) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setErrorMsg("Dismissing the challenge question triggers a security violation!");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, challengeEvent]);

  // Timer countdown implementation
  useEffect(() => {
    if (!isOpen || !eventId) return;
    setTimeLeft(timeLimit);
    setErrorMsg("");
    setResponseValue("");

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onTimeout(eventId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, eventId, timeLimit, onTimeout]);

  if (!isOpen || !challengeEvent) return null;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!responseValue.trim()) {
      setErrorMsg("Please enter an answer before submitting.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    try {
      await onSubmitAnswer(eventId, responseValue.trim());
    } catch (err) {
      console.error("Failed to submit challenge answer:", err);
      setErrorMsg("Failed to submit answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Prevent accidental backdrop clicks from closing, and trigger warning instead
  const handleBackdropClick = (e) => {
    e.stopPropagation();
    setErrorMsg("You must complete the challenge question to proceed.");
  };

  // Circular timer progress calculation
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (timeLeft / timeLimit) * circumference;

  // Visual status indicators based on time remaining
  const isTimeLow = timeLeft <= 5;
  const strokeColor = isTimeLow ? "stroke-error" : timeLeft <= 10 ? "stroke-warning" : "stroke-primary";
  const timerBgColor = isTimeLow ? "bg-red-100 text-error animate-pulse" : "bg-base-200 text-base-content";

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md transition-opacity duration-300"
      onClick={handleBackdropClick}
    >
      <div 
        className="relative w-full max-w-md bg-base-100 rounded-2xl shadow-2xl border border-base-300 overflow-hidden transform scale-100 transition-all duration-300 p-6 flex flex-col gap-6"
        onClick={(e) => e.stopPropagation()} // Stop propagation to avoid backdrop warning
      >
        {/* Header Alert Flag */}
        <div className="flex items-center gap-3 bg-warning/10 border border-warning/30 p-3 rounded-xl">
          <ShieldAlert className="w-6 h-6 shrink-0 text-warning" />
          <div className="text-xs font-semibold leading-relaxed text-amber-900">
            Identity Verification Challenge required to proceed. Failing or ignoring this triggers a violation log.
          </div>
        </div>

        {/* Question Prompt */}
        <div className="space-y-2">
          <span className="text-xs uppercase tracking-wider font-bold text-gray-500">Security Prompt</span>
          <h3 className="text-lg font-bold text-base-content">{prompt}</h3>
        </div>

        {/* Input Form & Circular Timer */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={responseValue}
                onChange={(e) => {
                  setResponseValue(e.target.value);
                  if (errorMsg) setErrorMsg("");
                }}
                placeholder="Type your response here..."
                autoFocus
                disabled={submitting}
                className="input input-bordered input-primary w-full text-base font-semibold tracking-wide placeholder:font-normal placeholder:tracking-normal"
              />
            </div>

            {/* Circular Countdown Progress SVG */}
            <div className={`relative flex items-center justify-center w-14 h-14 rounded-full ${timerBgColor} font-mono font-bold text-sm shrink-0 border border-base-300`}>
              <span className="absolute">{timeLeft}</span>
              <svg className="w-14 h-14 -rotate-90">
                <circle
                  cx="28"
                  cy="28"
                  r={radius}
                  className="stroke-base-300 fill-none"
                  strokeWidth="3"
                />
                <circle
                  cx="28"
                  cy="28"
                  r={radius}
                  className={`${strokeColor} fill-none transition-all duration-1000 ease-linear`}
                  strokeWidth="3"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          {errorMsg && (
            <div className="text-xs font-semibold text-error bg-error/10 p-2.5 rounded-lg border border-error/20">
              {errorMsg}
            </div>
          )}

          <div className="flex gap-3 justify-end items-center mt-6">
            <Button
              type="submit"
              isLoading={submitting}
              disabled={submitting || !responseValue.trim()}
              className="btn btn-primary w-full flex items-center justify-center gap-2 font-bold"
            >
              Verify Identity
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChallengeQuestionModal;
